import {
    FETCH_CARS,
    FETCH_CARS_SUCCESS,
    FETCH_CARS_FAILED,
    EDIT_CAR
  } from "../store/types";
  import store from '../store/store';
  import { firebase } from '../config/configureFirebase';
  import { onValue, update, set, child, remove, push, get } from "firebase/database";
  import { uploadBytesResumable, getDownloadURL } from "firebase/storage";

  /**
   * Resuelve el cuerpo de subida justo antes de usarlo.
   * Acepta Blob/File/Uint8Array o un factory async () => body.
   * En Android el Blob de XHR se cierra si se crea demasiado pronto (p. ej. al elegir
   * la foto o al preparar ambas imágenes antes de la 1.ª subida).
   */
  const resolveUploadBody = async (source) => {
    if (!source) return null;
    if (typeof source === 'function') {
      return await source();
    }
    return source;
  };

  const safeCloseBlob = (body) => {
    try {
      if (body && typeof body.close === 'function') body.close();
    } catch (e) { /* ignore */ }
  };
  
  export const fetchCars = () => (dispatch) => {
  
    const {
        carsRef
    } = firebase;
  
    const userInfo = store.getState().auth.profile;
    // AppCommon dispara fetchCars con delay; si el usuario cierra sesión antes, profile es null.
    if (!userInfo?.uid || !userInfo?.usertype) {
      return;
    }

    dispatch({
      type: FETCH_CARS,
      payload: null
    });

    onValue(carsRef(userInfo.uid, userInfo.usertype), snapshot => {
      if (snapshot.val()) {
        let data = snapshot.val();
        const arr = Object.keys(data).map(i => {
          data[i].id = i;
          return data[i]
        });
        dispatch({
          type: FETCH_CARS_SUCCESS,
          payload: arr
        });
      } else {
        dispatch({
          type: FETCH_CARS_FAILED,
          payload: store.getState().languagedata.defaultLanguage.no_cars
        });
      }
    });
  };
  
  export const editCar = (car, method) => async (dispatch) => {
    const {
      singleUserRef,
      carAddRef, 
      carEditRef,
      carImage
    } = firebase;
    dispatch({
      type: EDIT_CAR,
      payload: { method, car }
    });
    if (method === 'Add') {
        push(carAddRef, car);
    } else if (method === 'Delete') {
        remove(carEditRef(car.id));
    } else if (method === 'UpdateImage') {
      await uploadBytesResumable(carImage(car.id),car.car_image);
      let image = await getDownloadURL(carImage(car.id));
      let data = car;
      data.car_image = image;
      set(carEditRef(car.id), data);
      if(car.active && car.driver){
        update(singleUserRef(car.driver), {
          updateAt: new Date().getTime(),
          car_image: image
        });
      }   
    }
     else {
        set(carEditRef(car.id),car);
    }
  }

  // async: un blob ya cerrado hace que uploadBytesResumable lance de forma síncrona; así llega como rechazo.
  // carSource / registrationSource: Blob|Uint8Array|File o factory async () => body (recomendado en RN).
  export const updateUserCarWithImage = (newData, carSource, registrationSource) => async (dispatch) => {
    const {
      auth,
      carAddRef,
      singleUserRef,
      carImage,
      carRegistrationImage,
    } = firebase;

    var carId = push(carAddRef).key;
    let carBody = null;
    let regBody = null;

    try {
      carBody = await resolveUploadBody(carSource);
      if (!carBody) {
        throw new Error('image_upload_error');
      }
      await uploadBytesResumable(carImage(carId), carBody);
      safeCloseBlob(carBody);
      carBody = null;

      const url = await getDownloadURL(carImage(carId));
      newData.car_image = url;

      if (registrationSource) {
        // Regenerar DESPUÉS de la 1.ª subida: si ambos Blobs se crean juntos, Android cierra el 2.º.
        regBody = await resolveUploadBody(registrationSource);
        if (!regBody) {
          throw new Error('image_upload_error');
        }
        await uploadBytesResumable(carRegistrationImage(carId), regBody);
        safeCloseBlob(regBody);
        regBody = null;
        newData.registration_image = await getDownloadURL(carRegistrationImage(carId));
      }

      set(child(carAddRef, carId), newData);
      if (newData.active) {
        let updateData = {
          carType: newData.carType,
          vehicleNumber: newData.vehicleNumber,
          vehicleMake: newData.vehicleMake,
          vehicleModel: newData.vehicleModel,
          vehicleColor: newData.vehicleColor || '',
          other_info: newData.other_info,
          car_image: url,
          registration_image: newData.registration_image || null,
          carApproved: newData.approved,
          updateAt: new Date().getTime()
        };
        update(singleUserRef(auth.currentUser.uid), updateData);
      }
    } finally {
      safeCloseBlob(carBody);
      safeCloseBlob(regBody);
    }
  };

  /**
   * Admin aprueba un vehículo (limpia rechazo).
   * También lo deja active y sincroniza users.carApproved + datos del auto,
   * para que el conductor no siga viendo "espera por aprobación".
   */
  export const approveCar = (car) => async (dispatch) => {
    const { carEditRef, carsRef, singleUserRef } = firebase;
    if (!car?.id) return;

    // Desactivar otros autos activos del mismo conductor (solo uno activo).
    if (car.driver) {
      try {
        const snap = await get(carsRef(car.driver, 'driver'));
        const all = snap.val() || {};
        const deactivations = [];
        Object.keys(all).forEach((cid) => {
          if (cid === car.id) return;
          if (all[cid] && all[cid].active) {
            deactivations.push(
              update(carEditRef(cid), { active: false, updateAt: new Date().getTime() })
            );
          }
        });
        if (deactivations.length) await Promise.all(deactivations);
      } catch (e) {
        console.warn('[approveCar] no se pudieron desactivar otros autos', e);
      }
    }

    const next = {
      ...car,
      approved: true,
      active: true,
      rejected: false,
      rejection_code: null,
      rejection_reason: null,
      rejection_other: null,
      rejectedAt: null,
    };
    delete next.tableData;
    set(carEditRef(car.id), next);
    if (car.driver) {
      update(singleUserRef(car.driver), {
        carApproved: true,
        carType: car.carType || null,
        vehicleNumber: car.vehicleNumber || null,
        vehicleMake: car.vehicleMake || null,
        vehicleModel: car.vehicleModel || null,
        vehicleColor: car.vehicleColor || '',
        other_info: car.other_info || '',
        car_image: car.car_image || null,
        registration_image: car.registration_image || null,
        updateAt: new Date().getTime(),
      });
    }
    dispatch({ type: EDIT_CAR, payload: { method: 'Approve', car: next } });
  };

  /** Admin rechaza un vehículo con motivo. */
  export const rejectCar = (car, { code, reason, otherText } = {}) => async (dispatch) => {
    const { carEditRef, singleUserRef } = firebase;
    if (!car?.id || !code) return;
    const next = {
      ...car,
      approved: false,
      rejected: true,
      active: false,
      rejection_code: code,
      rejection_reason: reason || code,
      rejection_other: code === 'other' ? (otherText || '') : null,
      rejectedAt: new Date().getTime(),
    };
    delete next.tableData;
    set(carEditRef(car.id), next);
    if (car.driver && car.active) {
      update(singleUserRef(car.driver), {
        carApproved: false,
        updateAt: new Date().getTime(),
      });
    }
    dispatch({ type: EDIT_CAR, payload: { method: 'Reject', car: next } });
  };

  /**
   * Conductor reenvía tras rechazo: actualiza fotos (opcionales) y vuelve a pendiente.
   * carBlob / registrationBlob pueden ser null si no se cambian.
   */
  export const resubmitCarAfterRejection = (car, carSource, registrationSource) => async (dispatch) => {
    const {
      carEditRef,
      carImage,
      carRegistrationImage,
      singleUserRef,
    } = firebase;
    if (!car?.id) return;
    const next = {
      ...car,
      approved: false,
      rejected: false,
      rejection_code: null,
      rejection_reason: null,
      rejection_other: null,
      rejectedAt: null,
      resubmittedAt: new Date().getTime(),
    };
    delete next.tableData;
    let carBody = null;
    let regBody = null;
    try {
      if (carSource) {
        carBody = await resolveUploadBody(carSource);
        if (carBody) {
          await uploadBytesResumable(carImage(car.id), carBody);
          safeCloseBlob(carBody);
          carBody = null;
          next.car_image = await getDownloadURL(carImage(car.id));
        }
      }
      if (registrationSource) {
        regBody = await resolveUploadBody(registrationSource);
        if (regBody) {
          await uploadBytesResumable(carRegistrationImage(car.id), regBody);
          safeCloseBlob(regBody);
          regBody = null;
          next.registration_image = await getDownloadURL(carRegistrationImage(car.id));
        }
      }
    } finally {
      safeCloseBlob(carBody);
      safeCloseBlob(regBody);
    }
    set(carEditRef(car.id), next);
    if (car.driver && car.active) {
      update(singleUserRef(car.driver), {
        car_image: next.car_image,
        registration_image: next.registration_image || null,
        carApproved: false,
        updateAt: new Date().getTime(),
      });
    }
    dispatch({ type: EDIT_CAR, payload: { method: 'Resubmit', car: next } });
  };

  /** Admin / app: subir o reemplazar solo la tarjeta de propiedad. Devuelve la URL. */
  export const updateCarRegistrationImage = (car, blob) => async (dispatch) => {
    const {
      carEditRef,
      carRegistrationImage,
      singleUserRef,
    } = firebase;
    if (!car?.id || !blob) return null;
    await uploadBytesResumable(carRegistrationImage(car.id), blob);
    try { blob.close && blob.close(); } catch (e) { /* ignore */ }
    const url = await getDownloadURL(carRegistrationImage(car.id));
    const next = { ...car, registration_image: url };
    set(carEditRef(car.id), next);
    if (car.active && car.driver) {
      update(singleUserRef(car.driver), {
        registration_image: url,
        vehicleColor: car.vehicleColor || null,
        updateAt: new Date().getTime(),
      });
    }
    dispatch({
      type: EDIT_CAR,
      payload: { method: 'UpdateRegistrationImage', car: next },
    });
    return url;
  };